// app/components/Plugins/Backtester/Backtester.tsx
'use client'

import React, { useState } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import styles from './Backtest.module.css'

// Register Chart.js components.
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

/**
 * @uiComponent
 * @name Backtester
 * @description The Backtester component provides comprehensive historical strategy testing capabilities.
 * It allows users to test trading strategies against historical data, configure parameters like date ranges and initial capital,
 * and visualize performance through equity curves and key metrics like ROI, Sharpe ratio, and maximum drawdown.
 * @location Backtester plugin in the trading terminal
 * @prop {string} ticker - Optional initial ticker symbol to pre-populate the form
 */

/**
 * @uiWorkflow
 * @name Run Basic Strategy Backtest
 * @description How to test a trading strategy against historical market data
 * @platform backtester
 * @step 1 Navigate to the Backtester plugin [component: Backtester] [action: navigate]
 * @step 2 Enter the stock symbol to test [component: Backtester] [action: type] [notes: e.g., AAPL, MSFT, PAYTM.NS]
 * @step 3 Set the start date for backtesting [component: Backtester] [action: select] [notes: Choose historical start point]
 * @step 4 Set the end date for backtesting [component: Backtester] [action: select] [notes: Choose end point for analysis]
 * @step 5 Configure initial capital amount [component: Backtester] [action: type] [notes: Starting investment amount]
 * @step 6 Select trading strategy from dropdown [component: Backtester] [action: select] [notes: Buy and Hold, etc.]
 * @step 7 Click "Run Backtest" to start analysis [component: Backtester] [action: click]
 * @step 8 Wait for backtest to complete [component: Backtester] [action: wait] [notes: Processing historical data]
 * @step 9 Review performance metrics [component: Backtester] [action: observe] [notes: ROI, Sharpe ratio, max drawdown]
 * @step 10 Analyze the equity curve chart [component: Backtester] [action: examine] [notes: Visual performance over time]
 */

/**
 * @uiWorkflow
 * @name Compare Different Time Periods
 * @description How to analyze strategy performance across various market conditions
 * @platform backtester
 * @step 1 Run initial backtest with broad date range [component: Backtester] [action: execute] [notes: e.g., 5-10 years]
 * @step 2 Note the overall performance metrics [component: Backtester] [action: record]
 * @step 3 Modify start date to focus on bull market period [component: Backtester] [action: edit]
 * @step 4 Run backtest for bull market period [component: Backtester] [action: click]
 * @step 5 Compare metrics with overall performance [component: Backtester] [action: compare]
 * @step 6 Test bear market period separately [component: Backtester] [action: configure]
 * @step 7 Run backtest for bear market period [component: Backtester] [action: execute]
 * @step 8 Analyze strategy robustness across conditions [component: Backtester] [action: evaluate]
 */

/**
 * @uiWorkflow
 * @name Optimize Strategy Parameters
 * @description How to find optimal settings for trading strategies
 * @platform backtester
 * @step 1 Start with default strategy parameters [component: Backtester] [action: baseline]
 * @step 2 Run initial backtest and record results [component: Backtester] [action: execute]
 * @step 3 Modify initial capital to test scaling effects [component: Backtester] [action: adjust]
 * @step 4 Test with different capital amounts [component: Backtester] [action: iterate]
 * @step 5 Compare Sharpe ratios across tests [component: Backtester] [action: analyze] [notes: Higher is better]
 * @step 6 Identify optimal capital allocation [component: Backtester] [action: optimize]
 * @step 7 Test edge cases with extreme values [component: Backtester] [action: stress-test]
 * @step 8 Document best-performing configuration [component: Backtester] [action: record]
 */

interface BacktesterProps {
  ticker?: string
}
// Define the type for the API backtest result.
interface BacktestResult {
  metrics: {
    ROI: number
    sharpeRatio: number
    maxDrawdown: number
  }
  equityCurve: Array<{ date: string; equity: number }>
  strategyParameters: Record<string, any>
}

export default function Backtester({ ticker }: BacktesterProps = {}) {
  // Form state variables.
  const [symbol, setSymbol] = useState(ticker || 'BTCUSDT')
  const [startDate, setStartDate] = useState('2014-01-01')
  const [endDate, setEndDate] = useState('2025-01-01')
  const [initialCapital, setInitialCapital] = useState(10000)
  const [strategy, setStrategy] = useState('Buy and Hold')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<BacktestResult | null>(null)
  const [error, setError] = useState('')

  // Handler for running the backtest.
  const handleRunBacktest = async () => {
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const res = await fetch('/api/backtest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol,
          startDate,
          endDate,
          initialCapital,
          strategy
        })
      })
      if (!res.ok) {
        const errData = await res.json()
        setError(errData.error || 'Error running backtest')
        setLoading(false)
        return
      }
      const data = await res.json()
      setResult(data)
    } catch (err: any) {
      setError('Failed to run backtest: ' + err.message)
    }
    setLoading(false)
  }

  // Prepare chart data from the equity curve.
  const chartData = {
    labels: result?.equityCurve.map(point => point.date) || [],
    datasets: [
      {
        label: 'Equity Curve',
        data: result?.equityCurve.map(point => point.equity) || [],
        borderColor: 'rgba(75,192,192,1)',
        fill: false
      }
    ]
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.header}>Backtester</h2>
      <div className={styles.formGroup}>
        <label className={styles.label}>
          Symbol:
          <input
            type="text"
            value={symbol}
            onChange={e => setSymbol(e.target.value)}
            className={styles.input}
            placeholder="e.g., PAYTM.NS, BTCUSDT, ETHUSDT"
            title="Indian stocks: use .NS or .BS suffix (e.g., PAYTM.NS). Crypto: use no-suffix format (e.g., BTCUSDT)"
          />
          <small className={styles.helpText}>
            Indian stocks use PAYTM.NS (.NS/.BS) • Crypto pairs use BTCUSDT format
          </small>
        </label>
        <label className={styles.label}>
          Start Date:
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className={styles.input}
          />
        </label>
        <label className={styles.label}>
          End Date:
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className={styles.input}
          />
        </label>
        <label className={styles.label}>
          Initial Capital:
          <input
            type="number"
            value={initialCapital}
            onChange={e => setInitialCapital(Number(e.target.value))}
            className={styles.input}
          />
        </label>
        <label className={styles.label}>
          Strategy:
          <select
            value={strategy}
            onChange={e => setStrategy(e.target.value)}
            className={styles.select}
          >
            <option value="Buy and Hold">Buy and Hold</option>
            <option value="Moving Average Crossover">
              Moving Average Crossover
            </option>
            <option value="RSI Strategy">RSI Strategy</option>
            <option value="MACD Strategy">MACD Strategy</option>
            <option value="Bollinger Bands Strategy">
              Bollinger Bands Strategy
            </option>
          </select>
        </label>
      </div>
      <button
        onClick={handleRunBacktest}
        disabled={loading}
        className={styles.button}
      >
        {loading ? 'Running...' : 'Run Backtest'}
      </button>
      {error && <p className={styles.error}>{error}</p>}
      {result && (
        <div className={styles.results}>
          <h3 className={styles.resultsHeader}>Performance Metrics</h3>
          <p>ROI: {result.metrics.ROI.toFixed(2)}%</p>
          <p>Sharpe Ratio: {result.metrics.sharpeRatio.toFixed(2)}</p>
          <p>Max Drawdown: {result.metrics.maxDrawdown.toFixed(2)}%</p>
          
          <h3 className={styles.strategyHeader}>Strategy Parameters</h3>
          <div className={styles.strategyParams}>
            <p><strong>Strategy:</strong> {result.strategyParameters.strategy}</p>
            {result.strategyParameters.description && (
              <p><strong>Description:</strong> {result.strategyParameters.description}</p>
            )}
            {Object.entries(result.strategyParameters)
              .filter(([key]) => !['strategy', 'description'].includes(key))
              .map(([key, value]) => (
                <p key={key}>
                  <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value}
                </p>
              ))
            }
          </div>
          
          <h3 className={styles.resultsEquityHeader}>Equity Curve</h3>
          <div className={styles.chartContainer}>
            <Line data={chartData} options={{ responsive: true }} />
          </div>
        </div>
      )}
    </div>
  )
}
